import { useListFilters } from "../../hooks/useListState.js";
import { useMonthState } from "../../hooks/useMonthState.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeftOutlined, CalendarOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, FolderAddOutlined, FolderOutlined, LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { App as AntApp, Button, Card, DatePicker, Descriptions, Drawer, Form, Input, List, Popconfirm, Select, Space, Table, Tooltip, Typography } from "antd";
import { AppEmpty } from "../../components/Feedback.jsx";
import { RouteTargetNotice } from "../../components/RouteTargetNotice.jsx";
import { MonthlyCalendarGrid } from "../../components/MonthlyCalendarGrid.jsx";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { academyDateKey } from "../../utils/date.js";
import { getNextExamPeriodColor } from "../../utils/calendarColors.js";

import { ExamScheduleDrawer } from "./ExamScheduleDrawer.jsx";
import { examCalendarRanges, examGradeLabel, examPeriodsOnDate, examSchoolColor } from "./examCalendar.js";
import { examScheduleView } from "./examScheduleView.js";
import { loadExamFolders } from "../../lib/academyRepository.js";
import { downloadBlob } from "../../utils/files.js";
import { schoolDisplayName } from "../../utils/schoolDisplay.js";

dayjs.locale("ko");


const blankFolder = { year: String(new Date().getFullYear()), title: "" };
const blankPeriod = () => ({ school: "", schoolChoiceId: "", grade: "", inputStart: academyDateKey(), inputEnd: academyDateKey(), subjects: {} });

function getDayPlan(period, date) {
  const value = period.subjects?.[date];
  if (!value) return { subject: "", direct: "" };
  if (typeof value === "string") return { subject: value, direct: "" };
  return { subject: value.subject || "", direct: value.direct || "" };
}

export function ExamPeriodsPage({ store, navigation }) {
  const { state, update, createId } = store;
  const folders = state.examFolders || [];
  const routeFolderId = navigation?.route.kind === "examFolder" ? navigation.route.id : "";
  const [localFolderId, setLocalFolderId] = useState("");
  const selectedFolderId = navigation ? routeFolderId : localFolderId;
  const setSelectedFolderId = id => navigation ? (id ? navigation.open("examFolder", id) : navigation.close()) : setLocalFolderId(id);
  const [cursorMonth, setCursorMonth] = useMonthState(`month:exam:${selectedFolderId || "folders"}`, () => academyDateKey().slice(0, 7));
  const cursor = useMemo(() => dayjs(`${cursorMonth}-01`), [cursorMonth]);
  const setCursor = next => setCursorMonth(current =>
    (typeof next === "function" ? next(dayjs(`${current}-01`)) : next).format("YYYY-MM"));
  const [drag, setDrag] = useState(null);
  const [localDetailDate, setLocalDetailDate] = useState("");
  const detailDate = navigation?.route.panel === "examDate" ? navigation.route.entry : localDetailDate;
  const setDetailDate = date => {
    if (date && routeFolderId) navigation.openPanel("examDate", date);
    else if (!date && navigation?.route.panel === "examDate") navigation.close();
    else setLocalDetailDate(date);
  };
  const suppressDateClick = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState("");
  const [listFilters, setListFilters] = useListFilters(`list:exam:${selectedFolderId || "folders"}`, { school: "", grade: "" });
  const schoolFilter = listFilters.school, gradeFilter = listFilters.grade;
  const setSchoolFilter = school => setListFilters(v => ({ ...v, school }));
  const setGradeFilter = grade => setListFilters(v => ({ ...v, grade }));
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState(blankPeriod);
  const [folderAntForm] = Form.useForm();
  const { message } = AntApp.useApp();

  useEffect(() => {
    if (!drag) return;
    const cancelOutside = (event) => {
      if (event.type === "blur" || !event.target?.closest?.(".monthly-calendar-scroll")) setDrag(null);
    };
    window.addEventListener("mouseup", cancelOutside);
    window.addEventListener("blur", cancelOutside);
    return () => {
      window.removeEventListener("mouseup", cancelOutside);
      window.removeEventListener("blur", cancelOutside);
    };
  }, [drag]);

  const selectedFolder = folders.find((folder) => folder.id === selectedFolderId) || null;
  const allPeriods = selectedFolder?.periods || [];
  const view = useMemo(() => examScheduleView(allPeriods, { school: schoolFilter, grade: gradeFilter }), [allPeriods, schoolFilter, gradeFilter]);
  const periods = view.periods;
  useEffect(() => {
    setLocalDetailDate("");
  }, [selectedFolderId]);
  useEffect(() => {
    if (store.loading || store.error || !selectedFolder) return;
    if (schoolFilter && !view.schoolOptions.some((option) => option.value === schoolFilter)) {
      setSchoolFilter(""); setGradeFilter("");
    } else if (gradeFilter && !view.gradeOptions.some((option) => option.value === gradeFilter)) setGradeFilter("");
  }, [schoolFilter, gradeFilter, view.schoolOptions, view.gradeOptions, store.loading, store.error, selectedFolder]);
  const calendarPeriods = useMemo(() => examCalendarRanges(periods).map((range) => ({
    ...range,
    label: range.label.replace(range.schoolLabel, schoolDisplayName(range.schoolLabel, state.academyInfo)),
    getTitle: date => range.getTitle(date).replace(range.schoolLabel, schoolDisplayName(range.schoolLabel, state.academyInfo)),
    getLabel: (date, position) => range.getLabel(date, position) ? <>
      <span className="exam-calendar-school-label">{schoolDisplayName(range.schoolLabel, state.academyInfo)}</span>
      {range.gradeLabel && <span className="exam-calendar-grade-label">{range.gradeLabel}</span>}
    </> : ""
  })), [periods, state.academyInfo]);
  const folderName = selectedFolder ? `${selectedFolder.year} ${selectedFolder.title}` : "";

  const tableDates = view.dates;

  async function createFolder(values) {
    const year = values.year?.trim();
    const title = values.title?.trim();
    if (!year || !title) return;
    const id = createId("folder");
    if (!await update((draft) => {
      draft.examFolders.push({ id, year, title, periods: [] });
    })) return;
    folderAntForm.resetFields();
  }

  function openPeriodModal(range, period) {
    if (!selectedFolder) return message.warning({ content: "먼저 시험 일정 폴더를 선택해 주세요.", className: "exam-schedule-message" });
    const availablePeriods = period ? allPeriods.filter((item) => item.id !== period.id) : allPeriods;
    const automaticColor = period?.color || getNextExamPeriodColor(availablePeriods);
    const next = period
      ? { ...period, color: automaticColor, subjects: structuredClone(period.subjects || {}) }
      : { ...blankPeriod(), color: automaticColor, ...(range ? { inputStart: range.start < range.end ? range.start : range.end, inputEnd: range.start > range.end ? range.start : range.end } : {}) };
    setEditingPeriodId(period?.id || "");
    setForm(next);
    setModalOpen(true);
  }

  function closePeriodModal() {
    setModalOpen(false); setDrag(null); setEditingPeriodId(""); setForm(blankPeriod());
  }

  function finishDrag() {
    if (drag && drag.start !== drag.end) {
      suppressDateClick.current = true;
      openPeriodModal(drag);
      setTimeout(() => { suppressDateClick.current = false; }, 0);
    }
    setDrag(null);
  }

  function activateDate(date) {
    if (suppressDateClick.current) return;
    if (examPeriodsOnDate(periods, date).length) setDetailDate(date);
    else openPeriodModal({ start: date, end: date });
  }

  function moveMonth(amount) {
    setCursor((current) => current.add(amount, "month"));
  }

  async function removePeriod(id) {
    if (await store.deleteExamPeriod(id)) message.success({ content: "일정을 삭제했습니다. 기록은 이력으로 보존됩니다.", className: "exam-schedule-message" });
  }

  async function exportTable() {
    if (exporting || !view.rows.length) return;
    setExporting(true);
    try {
      const { reloadExamExport, examWorkbookBuffer, examXlsxFilename, examXlsxMime } = await import("./examWorkbook.js");
      const fresh = await reloadExamExport(selectedFolder, { school: schoolFilter, grade: gradeFilter }, view, loadExamFolders);
      const buffer = await examWorkbookBuffer(fresh.folder, fresh.view, { academyName: state.academyInfo?.name || "" });
      downloadBlob(examXlsxFilename(fresh.folder), new Blob([buffer], { type: examXlsxMime }));
    } catch (failure) {
      message.error({ content: failure.message?.startsWith("일정이 변경") || failure.message?.startsWith("이 폴더") || failure.message === "다운로드할 일정이 없습니다."
        ? failure.message : "엑셀을 다운로드하지 못했습니다. 새로고침 후 다시 시도해 주세요.", className: "exam-schedule-message" });
    } finally {
      setExporting(false);
    }
  }

  const periodSideColumns = [
    {
      title: "학교",
      dataIndex: "school",
      key: "school",
      ellipsis: true,
      render: (school, period) => (
        <span className="exam-period-side-school" title={schoolDisplayName(school, state.academyInfo)}>
          <i style={{ backgroundColor: examSchoolColor(period) }} />
          <strong>{schoolDisplayName(school, state.academyInfo)}</strong>
        </span>
      )
    },
    {
      title: "학년",
      dataIndex: "grade",
      key: "grade",
      width: 48,
      render: (grade) => examGradeLabel(grade)
    },
    {
      title: "시험기간",
      key: "period",
      width: 96,
      render: (_, period) => (
        <span className="exam-period-side-dates">
          {period.start ? <><time dateTime={period.start}>{period.start.replaceAll("-", ".")}</time><time dateTime={period.end}>~ {period.end.replaceAll("-", ".")}</time></> : "시험 없음 (직보만)"}
        </span>
      )
    },
    {
      title: "",
      key: "actions",
      width: 64,
      align: "right",
      render: (_, period) => (
        <Space size={0}>
          <Button type="text" size="small" icon={<EditOutlined />} aria-label={schoolDisplayName(period.school, state.academyInfo) + " 시험 일정 수정"} onClick={() => openPeriodModal(null, period)} />
          <Popconfirm
            title="시험 일정을 삭제할까요?"
            okText="삭제"
            cancelText="취소"
            okButtonProps={{ danger: true }}
            onConfirm={() => removePeriod(period.id)}
          >
            <Button disabled={store.saving} danger type="text" size="small" icon={<DeleteOutlined />} aria-label={schoolDisplayName(period.school, state.academyInfo) + " 시험 일정 삭제"} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const scheduleColumns = [
    { title: "학교", dataIndex: "school", key: "school", fixed: "left", width: 120, align: "left", onHeaderCell: () => ({ style: { textAlign: "left" } }), render: (school) => <strong className="exam-period-school-name">{schoolDisplayName(school, state.academyInfo)}</strong> },
    { title: "학년", dataIndex: "grade", key: "grade", fixed: "left", width: 72, align: "center", render: (grade) => examGradeLabel(grade) },
    ...tableDates.map((date) => ({
      title: <span className="exam-schedule-date-heading">{dayjs(date).format("M/D(ddd)")}</span>,
      key: date,
      align: "center",
      width: 96,
      render: (_, row) => {
        const plan = row.cells[date];
        if (!plan.inRange) return null;
        return (
          <div className="exam-cell-plan" style={{ background: `${row.color || "#64748b"}22` }}>
            {plan.subjects.map((subject) => <span key={subject} className="exam-subject">{subject}</span>)}
            {plan.direct.map((direct) => <span key={direct} className="exam-direct">{direct} 직보</span>)}
          </div>
        );
      }
    }))
  ];

  if (routeFolderId && !selectedFolder) return <RouteTargetNotice loading={store.loading} onBack={() => navigation.close()} />;
  if (!selectedFolder) {
    return (
      <div className="exam-folder-home">
        <Card className="academy-management-card exam-folder-list-card">
          <div className="academy-tab-panel">
            <div className="section-toolbar">
              <h3>시험 일정 폴더</h3>
              <Typography.Text type="secondary">총 {folders.length}개</Typography.Text>
            </div>
            {folders.length ? (
            <List
              dataSource={folders}
              renderItem={(folder) => (
                <List.Item>
                  <button
                    type="button"
                    className="exam-folder-button"
                    onClick={() => setSelectedFolderId(folder.id)}
                  >
                    <FolderOutlined className="exam-folder-icon" />
                    <span className="exam-folder-text">
                      <Typography.Text strong>{folder.year} {folder.title}</Typography.Text>
                      <Typography.Text type="secondary">{folder.periods.length}개 학교 일정</Typography.Text>
                    </span>
                    <span className="exam-folder-open">
                      열기
                      <RightOutlined />
                    </span>
                  </button>
                </List.Item>
              )}
            />
          ) : (
            <AppEmpty description="아직 만들어진 시험 일정 폴더가 없습니다." />
            )}
          </div>
        </Card>

        <Card className="academy-management-card exam-folder-create-card">
          <div className="academy-tab-panel">
            <div className="section-toolbar">
              <h3>시험 일정 폴더 만들기</h3>
            </div>
            <Typography.Paragraph type="secondary">시험 기간 단위로 폴더를 만들고 학교별 일정을 등록합니다.</Typography.Paragraph>
          <Form form={folderAntForm} layout="vertical" initialValues={blankFolder} onFinish={createFolder}>
            <Form.Item name="year" label="년도" rules={[{ required: true, message: "년도를 입력하세요." }]}>
              <Input placeholder="2026" />
            </Form.Item>
            <Form.Item name="title" label="제목" rules={[{ required: true, message: "제목을 입력하세요." }]}>
              <Input placeholder="1학기 중간고사" />
            </Form.Item>
            <Form.Item>
              <Button block type="primary" htmlType="submit" icon={<FolderAddOutlined />}>폴더 만들기</Button>
            </Form.Item>
            </Form>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-stack exam-period-page">
      <div className="exam-period-back-row">
        <Button className="exam-period-back-button" icon={<ArrowLeftOutlined />} onClick={() => { setLocalDetailDate(""); setSelectedFolderId(""); }}>폴더 목록</Button>
      </div>

      <Card className="academy-management-card exam-period-context-card">
        <div className="exam-period-context-bar">
          <div className="exam-period-context-identity">
            <div className="exam-period-context-title">
              <strong>{folderName}</strong>
              <span>학교별 시험 일정 관리</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="academy-management-card exam-period-table-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <div className="exam-support-heading">
              <h3>학교별 일정표</h3>
              <span>{folderName}</span>
            </div>
            <Space className="section-toolbar-actions" size={8} wrap>
              <Button disabled={!view.rows.length || store.saving} loading={exporting} aria-label="엑셀 다운로드" icon={<DownloadOutlined />} onClick={exportTable}>엑셀 다운로드</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openPeriodModal()}>일정 추가</Button>
            </Space>
          </div>
          <div className="exam-schedule-filters">
            <div className={`student-filter-control exam-schedule-filter-field${schoolFilter ? " is-active" : ""}`}><span aria-hidden="true">학교:</span><label className="exam-filter-accessible-label" htmlFor="exam-school-filter">학교 필터</label><Select id="exam-school-filter" showSearch optionFilterProp="label" allowClear placeholder="전체 학교" value={schoolFilter || undefined}
              options={view.schoolOptions.map(option => ({ ...option, label: schoolDisplayName(option.label, state.academyInfo) }))} onChange={(value) => { setSchoolFilter(value || ""); setGradeFilter(""); setDetailDate(""); }}
              optionRender={(option) => <Tooltip title={`${option.data.legacy ? "미연결 기록" : "학교 식별자"}: ${option.data.identity}`}>{option.label}</Tooltip>} /></div>
            <div className={`student-filter-control exam-schedule-filter-field${gradeFilter ? " is-active" : ""}`}><span aria-hidden="true">학년:</span><label className="exam-filter-accessible-label" htmlFor="exam-grade-filter">학년 필터</label><Select id="exam-grade-filter" allowClear placeholder="전체 학년" value={gradeFilter || undefined} options={view.gradeOptions}
              onChange={(value) => { setGradeFilter(value || ""); setDetailDate(""); }} /></div>
            <Button className="student-filter-reset" onClick={() => { setSchoolFilter(""); setGradeFilter(""); setDetailDate(""); }}>초기화</Button>
          </div>
          {view.sections.length ? <div className="exam-schedule-sections">{view.sections.map((section) => (
            <section key={section.key} className="exam-schedule-section" data-course={section.key} aria-label={`${section.label} 일정표`}>
              <h4>{section.label}</h4>
              <Table className="management-list-table exam-period-table" rowKey="key" columns={scheduleColumns}
                dataSource={section.rows} pagination={false} scroll={{ x: "max-content" }} />
            </section>
          ))}</div> : <AppEmpty description="표시할 시험 일정이 없습니다." />}
        </div>
      </Card>

      <div className="calendar-page-grid exam-calendar-workspace">
        <Card className="academy-management-card exam-calendar-card">
          <div className="exam-calendar-heading">
            <div>
              <h3>시험 일정 캘린더</h3>
            </div>
            <span className="exam-calendar-drag-hint">날짜를 드래그해 시험기간을 추가할 수 있어요.</span>
          </div>
          <div className="exam-calendar-navigation-row">
            <div className="payment-month-selector payment-period-selector exam-period-month-selector">
              <Button type="text" icon={<LeftOutlined />} aria-label="이전 달" onClick={() => moveMonth(-1)} />
              <DatePicker
                className="payment-month-picker"
                picker="month"
                value={cursor}
                onChange={(value) => value && setCursor(value)}
                format="YYYY년 M월"
                allowClear={false}
                inputReadOnly
                suffixIcon={<CalendarOutlined />}
              />
              <Button type="text" icon={<RightOutlined />} aria-label="다음 달" onClick={() => moveMonth(1)} />
            </div>
          </div>
          <MonthlyCalendarGrid
            month={cursor}
            ranges={calendarPeriods}
            ariaItemLabel="시험 일정"
            maxVisibleRanges={3}
            isDateHighlighted={(dateKey) => Boolean(drag && dateKey >= (drag.start < drag.end ? drag.start : drag.end) && dateKey <= (drag.start > drag.end ? drag.start : drag.end))}
            onDateActivate={activateDate}
            onDateSelect={activateDate}
            onDateMouseDown={(dateKey, _, event) => event.button === 0 && setDrag({ start: dateKey, end: dateKey })}
            onDateMouseEnter={(dateKey) => drag && setDrag((current) => current ? { ...current, end: dateKey } : current)}
            onMouseUp={finishDrag}
          />
        </Card>

        <Card className="academy-management-card exam-period-list-card">
          <div className="academy-tab-panel">
            <div className="section-toolbar">
              <div className="exam-support-heading">
                <h3>{"\uD559\uAD50\uBCC4 \uC2DC\uD5D8\uAE30\uAC04 \uBAA9\uB85D"}</h3>
                <span>{periods.length}개 일정</span>
              </div>
            </div>
            <Table
              className="management-list-table exam-period-side-table"
              rowKey="id"
              columns={periodSideColumns}
              dataSource={periods}
              pagination={false}
              size="small"
              tableLayout="fixed"
              locale={{ emptyText: <AppEmpty description="등록된 학교별 시험 일정이 없습니다." /> }}
            />
          </div>
        </Card>
      </div>

      <Drawer className="exam-date-detail-drawer" title={`${detailDate ? dayjs(detailDate).format("YYYY.MM.DD (ddd)") : ""} 시험 일정`} open={Boolean(detailDate)} onClose={() => setDetailDate("")} width={520}>
        <List dataSource={examPeriodsOnDate(periods, detailDate)} locale={{ emptyText: <AppEmpty description="표시할 시험 일정이 없습니다." /> }} renderItem={(period) => (
          <List.Item key={period.id} data-period-id={period.id} className="exam-date-detail-item">
            <div className="exam-date-detail-heading">
              <i style={{ backgroundColor: examSchoolColor(period) }} aria-hidden="true" />
              <Typography.Text strong>{schoolDisplayName(period.school, state.academyInfo)} · {examGradeLabel(period.grade)}</Typography.Text>
            </div>
            <Descriptions size="small" column={1} items={[
              { key: "period", label: "시험기간", children: `${period.start} ~ ${period.end}` },
              { key: "subject", label: "시험 과목", children: getDayPlan(period, detailDate).subject },
              { key: "direct", label: "직보 과목", children: getDayPlan(period, detailDate).direct }
            ]} />
          </List.Item>
        )} />
      </Drawer>
      {modalOpen && <ExamScheduleDrawer initial={form} folderId={selectedFolder.id} store={store} onClose={closePeriodModal} />}
    </div>
  );
}
